import customersService from '@/services/customers';
import financialAccountsService from '@/services/financialAccounts';
import generalCatalogsService from '@/services/generalCatalogs';
import paymentReceiptsService from '@/services/paymentReceipts';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';

export const createDefaultPaymentReceiptsFilterOptions = () => ({
  customer: [],
  paymentForm: [],
  paymentAccount: [],
});

const normalizeRow = (row) => ({
  ...row,
  customer_id: Number(row?.customer_id || 0),
  payment_account_id: Number(row?.payment_account_id || 0),
  total: Number(row?.total || 0),
});

const normalizePaymentFormOptions = (list = []) => {
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      const code = String(item?.code || item?.id || '').trim().toUpperCase();
      const name = String(item?.name || '').trim();

      if (code === '') {
        return null;
      }

      return {
        value: code,
        label: name !== '' ? `${code} - ${name}` : code,
        meta: item,
      };
    })
    .filter(Boolean);
};

const extractSnapshotMetrics = (response) => {
  const payload = extractPayload(response);
  const metrics = payload && typeof payload === 'object'
    ? payload.metrics
    : null;

  return metrics && typeof metrics === 'object' ? metrics : {};
};

export const buildPaymentReceiptsListParams = ({ toolbarSearch = '', toolbarFilters = {} } = {}) => {
  const filters = toolbarFilters && typeof toolbarFilters === 'object' ? toolbarFilters : {};

  return {
    date_start: String(filters.date_from || '').trim(),
    date_end: String(filters.date_to || '').trim(),
    customer_id: Array.isArray(filters.customer_ids) ? filters.customer_ids : [],
    payment_form: Array.isArray(filters.payment_form_codes) ? filters.payment_form_codes : [],
    financial_account_id: Array.isArray(filters.payment_account_ids) ? filters.payment_account_ids : [],
  };
};

export const fetchPaymentReceiptsSnapshot = async (params = {}) => {
  const response = await paymentReceiptsService.index(params);
  const rows = extractCollection(response);

  return {
    rows: (Array.isArray(rows) ? rows : []).map((row) => normalizeRow(row)),
    metrics: extractSnapshotMetrics(response),
  };
};

export const fetchPaymentReceiptsRows = async (params = {}) => {
  const snapshot = await fetchPaymentReceiptsSnapshot(params);
  return Array.isArray(snapshot?.rows) ? snapshot.rows : [];
};

export const fetchPaymentReceiptsFilterOptions = async () => {
  const defaults = createDefaultPaymentReceiptsFilterOptions();

  try {
    const [customersResponse, paymentFormsResponse, paymentAccountsResponse] = await Promise.all([
      customersService.selectors?.() || Promise.resolve({ data: [] }),
      generalCatalogsService.getFormasPago?.() || Promise.resolve({ data: [] }),
      financialAccountsService.selectors?.({}, { skipLoading: true }) || Promise.resolve({ data: [] }),
    ]);

    const customerOptions = normalizeCatalogOptions(extractCollection(customersResponse), { useCodeInLabel: false });
    const paymentFormOptions = normalizePaymentFormOptions(extractCollection(paymentFormsResponse));
    const paymentAccountOptions = normalizeCatalogOptions(extractCollection(paymentAccountsResponse), { useCodeInLabel: false });

    return {
      ...defaults,
      customer: customerOptions,
      paymentForm: paymentFormOptions,
      paymentAccount: paymentAccountOptions,
    };
  } catch (error) {
    return defaults;
  }
};
